import { useState, useRef, useCallback } from 'react';

export default function BeforeAfterSlider() {
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPos(percent);
  }, []);

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging.current) handleMove(e.clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches && e.touches.length > 0) handleMove(e.touches[0].clientX);
  };

  const beforeImage =
    'https://static.readdy.ai/image/b35b46597ae7b9f4c7fd1e38dab17f7e/07fee43a85bfed226fd03da6e1726e8f.png';
  const afterImage =
    'https://static.readdy.ai/image/b35b46597ae7b9f4c7fd1e38dab17f7e/4c629cf7ee81dbaa61f5bde6f5e33daa.png';

  return (
    <section className="py-24 bg-brand-brown">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-brand-beige/70 text-sm font-semibold tracking-widest uppercase mb-4">
            Transformation
          </p>
          <h2
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Caprasimo, cursive' }}
          >
            Von Standard zu Premium.
          </h2>
          <p className="text-brand-beige/80 text-lg max-w-xl mx-auto">
            Slider ziehen oder klicken zum Vergleichen.
          </p>
        </div>

        <div
          ref={containerRef}
          className="relative w-full aspect-[3/2] rounded-2xl overflow-hidden cursor-col-resize select-none shadow-2xl"
          onMouseDown={(e) => { handleMouseDown(); handleMove(e.clientX); }}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onMouseMove={handleMouseMove}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleMouseUp}
        >
          {/* After image (full background) */}
          <img
            src={afterImage}
            alt="Nachher – Premium Webdesign"
            className="absolute inset-0 w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
            draggable={false}
          />

          {/* Before image (clipped) */}
          <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
            <img
              src={beforeImage}
              alt="Vorher – Standard Webseite"
              className="absolute inset-0 w-full h-full object-cover object-top"
              style={{
                width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100vw',
                maxWidth: 'none',
              }}
              loading="lazy"
              decoding="async"
              draggable={false}
            />
          </div>

          {/* Labels */}
          <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full pointer-events-none">
            Vorher
          </div>
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-brand-black text-xs font-semibold px-3 py-1.5 rounded-full pointer-events-none">
            Nachher
          </div>

          {/* Slider line */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white/90 pointer-events-none"
            style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}
          />

          {/* Slider handle */}
          <div
            className="absolute top-1/2 -translate-y-1/2 z-10 pointer-events-none"
            style={{ left: `${sliderPos}%`, transform: 'translateX(-50%) translateY(-50%)' }}
          >
            <div className="w-12 h-12 rounded-full bg-white shadow-xl flex items-center justify-center gap-1">
              <i className="ri-arrow-left-s-line text-brand-black text-lg w-5 h-5 flex items-center justify-center" />
              <i className="ri-arrow-right-s-line text-brand-black text-lg w-5 h-5 flex items-center justify-center" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
